/**
 * Хранение состояния чатов AI Tutor в localStorage.
 *
 * localStorage используется ТОЛЬКО для хранения последнего открытого session_id
 * по каждому контексту (страница/урок/задача). Вся история чатов, сообщения,
 * suggested questions и квизы хранятся в БД и загружаются через API.
 */

const LAST_CHAT_KEY_PREFIX = "ai-tutor-last-chat-";

/** Вспомогательные типы для sidebar */
export type ChatGroupKey = "courses" | "simulator" | "tutor";

export interface ChatListItem {
  /** session_id из бэкенда (UUID) */
  sessionId: string;
  title: string | null;
  updatedAt: string;
  contextType: string;
  courseSlug?: string | null;
  lessonId?: string | null;
  taskId?: string | null;
}

export function contextToGroup(contextType: string): ChatGroupKey {
  if (contextType === "course" || contextType === "lesson") return "courses";
  if (contextType === "editor" || contextType === "tasklist") return "simulator";
  return "tutor";
}

export function groupChatsByPage(chats: ChatListItem[]): Record<ChatGroupKey, ChatListItem[]> {
  const groups: Record<ChatGroupKey, ChatListItem[]> = {
    courses: [],
    simulator: [],
    tutor: [],
  };
  for (const c of chats) {
    const key = contextToGroup(c.contextType);
    groups[key].push(c);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// localStorage: last opened session_id per context
// ---------------------------------------------------------------------------

function buildLastChatKey(
  contextType: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): string {
  if (contextType === "course" && courseSlug) return `${LAST_CHAT_KEY_PREFIX}course-${courseSlug}`;
  if (contextType === "lesson" && lessonId) return `${LAST_CHAT_KEY_PREFIX}lesson-${lessonId}`;
  if (contextType === "editor" && taskId != null) return `${LAST_CHAT_KEY_PREFIX}editor-${taskId}`;
  if (contextType === "tasklist") return `${LAST_CHAT_KEY_PREFIX}tasklist`;
  return `${LAST_CHAT_KEY_PREFIX}generic`;
}

/** Возвращает session_id последнего открытого чата для данного контекста (из localStorage). */
export function getLastOpenedSessionId(
  contextType: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): string | null {
  try {
    return localStorage.getItem(buildLastChatKey(contextType, courseSlug, lessonId, taskId));
  } catch {
    return null;
  }
}

/** Сохраняет session_id как последний открытый чат для данного контекста. */
export function setLastOpenedSessionId(
  contextType: string,
  sessionId: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): void {
  try {
    localStorage.setItem(buildLastChatKey(contextType, courseSlug, lessonId, taskId), sessionId);
  } catch {
    // ignore
  }
}

/** Очищает last opened session id для данного контекста (при удалении чата). */
export function clearLastOpenedSessionId(
  contextType: string,
  sessionId: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): void {
  try {
    const key = buildLastChatKey(contextType, courseSlug, lessonId, taskId);
    if (localStorage.getItem(key) === sessionId) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

/** Загружает список чатов пользователя из API. */
export async function fetchChatList(
  apiHost: string,
  userId: number,
  contextType?: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): Promise<ChatListItem[]> {
  const params = new URLSearchParams({ user_id: String(userId) });
  if (contextType) params.set("context_type", contextType);
  if (courseSlug) params.set("course_slug", courseSlug);
  if (lessonId) params.set("lesson_id", lessonId);
  if (taskId != null) params.set("task_id", String(taskId));
  const res = await fetch(`${apiHost}/api/ai/chats?${params}`);
  if (!res.ok) throw new Error(`fetchChatList failed: ${res.status}`);
  const data = await res.json();
  return (data.chats as Array<{
    session_id: string;
    title: string | null;
    updated_at: string;
    context_type: string;
    course_slug?: string | null;
    lesson_id?: string | null;
    task_id?: string | null;
  }>).map((c) => ({
    sessionId: c.session_id,
    title: c.title,
    updatedAt: c.updated_at,
    contextType: c.context_type,
    courseSlug: c.course_slug,
    lessonId: c.lesson_id,
    taskId: c.task_id,
  }));
}

/** Все чаты пользователя (без фильтра по контексту) — для сайдбара с группами «Курсы / Тренажёр / Tutor». */
export async function fetchAllChatList(apiHost: string, userId: number): Promise<ChatListItem[]> {
  return fetchChatList(apiHost, userId);
}

/** Чат относится к текущей странице (курс / урок / задача / …). */
export function chatListItemMatchesPageContext(
  c: ChatListItem,
  contextType: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): boolean {
  if (c.contextType !== contextType) return false;
  if (contextType === "course") return (c.courseSlug || "") === (courseSlug || "");
  if (contextType === "lesson") {
    return (c.lessonId || "") === (lessonId || "") && (c.courseSlug || "") === (courseSlug || "");
  }
  if (contextType === "editor") return String(c.taskId ?? "") === String(taskId ?? "");
  if (contextType === "tasklist") return true;
  if (contextType === "generic") return true;
  return true;
}

/**
 * Какую сессию открыть при заходе на страницу: lastOpened из localStorage или самый свежий чат,
 * подходящий под текущий контекст (не первый чат из общего списка).
 */
export function selectSessionForPageContext(
  list: ChatListItem[],
  contextType: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): string | null {
  const last = getLastOpenedSessionId(contextType, courseSlug, lessonId, taskId);
  if (last && list.some((x) => x.sessionId === last)) return last;
  const matching = list.filter((c) =>
    chatListItemMatchesPageContext(c, contextType, courseSlug, lessonId, taskId),
  );
  matching.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  if (matching[0]?.sessionId) return matching[0].sessionId;

  // Урок: не начинать новый диалог при переходе со страницы курса — продолжить чат курса того же slug
  if (contextType === "lesson" && courseSlug) {
    const courseLast = getLastOpenedSessionId("course", courseSlug, null, null);
    if (courseLast) {
      const row = list.find((x) => x.sessionId === courseLast);
      if (
        row &&
        row.contextType === "course" &&
        (row.courseSlug || "") === (courseSlug || "")
      ) {
        return courseLast;
      }
    }
    const courseChats = list.filter(
      (c) => c.contextType === "course" && (c.courseSlug || "") === (courseSlug || ""),
    );
    courseChats.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return courseChats[0]?.sessionId ?? null;
  }

  return null;
}

/** Удаляет чат из БД и очищает localStorage. */
export async function deleteChatSessionOnServer(
  apiHost: string,
  sessionId: string,
  contextType?: string,
  courseSlug?: string | null,
  lessonId?: string | null,
  taskId?: string | number | null,
): Promise<boolean> {
  try {
    const res = await fetch(`${apiHost}/api/ai/chats/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
    if (res.ok && contextType) {
      clearLastOpenedSessionId(contextType, sessionId, courseSlug, lessonId, taskId);
    }
    return res.ok;
  } catch {
    return false;
  }
}

/** Сохраняет ответ пользователя на квиз. */
export async function saveQuizAnswer(
  apiHost: string,
  sessionId: string,
  quizId: number,
  answerIndex: number,
): Promise<{ ok: boolean; correct?: boolean }> {
  try {
    const res = await fetch(
      `${apiHost}/api/ai/chats/${encodeURIComponent(sessionId)}/quiz/${quizId}/answer`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer_index: answerIndex }),
      },
    );
    if (!res.ok) return { ok: false };
    return await res.json();
  } catch {
    return { ok: false };
  }
}
